import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import HighchartsReact from 'highcharts-react-official';
import { t } from 'i18next';

import { DateRangeType } from '../../../common/components/DateRangeTypePicker';
import { DISABLE_PULL_TO_REFRESH_ATTRIBUTE } from '../../../common/components/pull-to-refresh/constants';
import { StatefulChart } from '../../../common/components/StatefulChart';
import { DAY_RESET_ZOOM_ANIM_MS, HISTORY_CHART_OPTIONS } from '../constants';
import { ChargingActivityData } from '../types';
import { useChargingHistoryChartData, useChargingHistorySeries } from './use-charging-history-chart-data';

type Props = {
  readonly isLoaded: boolean;
  readonly hasFetchError: boolean;
  readonly selectedDateRangeType: DateRangeType;
  readonly rangeTypeToSelectedPeriod: Record<DateRangeType, Date>;
  readonly data: ChargingActivityData;
  readonly onChartChange: (isCostViewSelected: boolean) => void;
  readonly isCostViewSelected: boolean;
  readonly height?: number;
  readonly onDrillDown?: (date: Date) => void;
};

export function HistoryChart({
  isLoaded,
  hasFetchError,
  selectedDateRangeType,
  rangeTypeToSelectedPeriod,
  data,
  onChartChange,
  isCostViewSelected,
  height: heightProp,
  onDrillDown,
}: Props) {
  const color = useColorModeValue('#1E79B6', '#42B4E6');
  const chartHeight = heightProp ?? HISTORY_CHART_OPTIONS.height;
  const chartOpts = useMemo(() => ({ ...HISTORY_CHART_OPTIONS, height: chartHeight }), [chartHeight]);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const isDayView = selectedDateRangeType === DateRangeType.Day;
  const selectedDay = rangeTypeToSelectedPeriod[selectedDateRangeType];

  useEffect(() => {
    // The chart instance persists across day changes within the day view (only `selectedDateRangeType`
    // remounts it), so a previous zoom's extremes must be reset explicitly or they'll carry over silently.
    chartRef.current?.chart?.xAxis[0]?.setExtremes(undefined, undefined, false, false);
    setIsZoomed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDateRangeType, selectedDay]);

  const { energyConsumedSeries } = useChargingHistorySeries(data, selectedDateRangeType, isCostViewSelected);

  const options = useChargingHistoryChartData({
    series: energyConsumedSeries,
    selectedDateRangeType,
    rangeTypeToSelectedPeriod,
    chartOpts,
    isCostViewSelected,
    onDrillDown,
    onZoomChange: ({ isZoomed: zoomed }) => {
      setIsZoomed(zoomed);
    },
  });
  const isData = !hasFetchError;

  const handleResetZoom = () => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    chart.xAxis[0].setExtremes(undefined, undefined, true, { duration: DAY_RESET_ZOOM_ANIM_MS });
    setIsZoomed(false);
  };

  return (
    <>
      {isData && (
        <Flex fontSize={12} flexDirection="row" mb={2} pt={3}>
          <Text fontWeight={600}>{t('Activity.chart view').toUpperCase()}:</Text>
          {!isDayView && (
            <>
              <Box onClick={() => onChartChange(true)} ml={2} data-testid="chart-view-cost-btn">
                <Text color={isCostViewSelected ? color : 'current'}>{t('Common.cost').toUpperCase()}</Text>
              </Box>
              <Text ml={2}>|</Text>
            </>
          )}
          <Box ml={2} onClick={() => onChartChange(false)} data-testid="chart-view-kw-btn">
            <Text color={!isCostViewSelected || isDayView ? color : 'current'}>{isDayView ? 'kW' : 'kWh'}</Text>
          </Box>
        </Flex>
      )}
      <Box
        position="relative"
        {...(isDayView ? { [DISABLE_PULL_TO_REFRESH_ATTRIBUTE]: true } : {})}
        sx={{
          '.highcharts-reset-zoom': { display: 'none' },
          '.hc-pinching .highcharts-crosshair, .hc-pinching .highcharts-tooltip': { visibility: 'hidden' },
        }}
      >
        {isDayView && isZoomed && isData && (
          <Button
            size="md"
            variant="solid"
            position="absolute"
            top={2}
            right={2}
            zIndex={2}
            px={5}
            borderRadius="full"
            bg={color}
            color="white"
            fontWeight={700}
            boxShadow="lg"
            _hover={{ bg: color, opacity: 0.9 }}
            _active={{ bg: color, opacity: 0.8 }}
            onClick={handleResetZoom}
            data-testid="reset-zoom-btn"
          >
            {t('Activity.reset zoom')}
          </Button>
        )}
        <StatefulChart
          key={selectedDateRangeType}
          chartRef={chartRef}
          isLoaded={isLoaded}
          isData={isData}
          height={chartHeight}
          options={options}
          testId={'charging-history-chart-container'}
        />
      </Box>
    </>
  );
}
